import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, X, FileText, Wrench, Building2, Mail, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Send a Sterile Mate PO for a DI tank exchange — one PO per vendor, each with
// its own PO number, drafted into Outlook so the admin attaches the PO PDF.
export default function DIPoDispatchDialog({ diRequestId, backorderId, deploymentId, token, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [vendorTab, setVendorTab] = useState("supplier");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState({}); // id -> {vendor, po_number}
  const [form, setForm] = useState(null);
  const [previews, setPreviews] = useState([]);

  const authHeader = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  useEffect(() => {
    let alive = true;
    const qs = diRequestId
      ? `di_request_id=${diRequestId}`
      : deploymentId
        ? `deployment_id=${deploymentId}`
        : `backorder_id=${backorderId}`;
    axios.get(`${API}/api/admin/di-po/context?${qs}`, { headers: authHeader })
      .then((r) => {
        if (!alive) return;
        setCtx(r.data);
        setForm({
          hospital_po_number: r.data.hospital_po_number || "",
          service_requested: r.data.service_requested || "",
          service_code: r.data.service_code || "REPAIR",
          quantity: r.data.quantity || 2,
          quantity_label: "Tanks",
          replenishment_type: r.data.replenishment_type || "Standard Exchange",
          urgent: !!r.data.urgent,
          location: r.data.site?.location || "",
          destination: r.data.site?.destination || "",
          checkin_protocol: r.data.site?.checkin_protocol || "",
          poc_name: r.data.site?.poc_name || "",
          poc_phone: r.data.site?.poc_phone || "",
          sign_off_name: r.data.sign_off_name || "",
          extra_note: "",
        });
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Failed to load PO context");
        onClose?.();
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [diRequestId, backorderId, deploymentId, authHeader, onClose]);

  const list = vendorTab === "contractor" ? (ctx?.contractors || []) : (ctx?.suppliers || []);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((v) => `${v.name} ${v.email} ${(v.categories || []).join(" ")}`.toLowerCase().includes(q));
  }, [list, search]);

  const toggle = (v) => setPicked((p) => {
    const next = { ...p };
    if (next[v.id]) delete next[v.id];
    else next[v.id] = { vendor: v, po_number: "" };
    return next;
  });
  const setPo = (id, po) => setPicked((p) => ({ ...p, [id]: { ...p[id], po_number: po } }));

  const chosen = Object.values(picked);
  const basePayload = () => ({
    di_request_id: diRequestId || null,
    backorder_id: backorderId || null,
    deployment_id: deploymentId || null,
    hospital_key: ctx?.hospital_key,
    hospital_name: ctx?.hospital_name || "",
    ...form,
    quantity: Number(form.quantity) || 1,
    vendors: chosen.map((c) => ({
      id: c.vendor.id, name: c.vendor.name, email: c.vendor.email,
      vendor_type: c.vendor.vendor_type, po_number: c.po_number,
    })),
  });

  const preview = async () => {
    if (!chosen.length) return toast.error("Select at least one vendor");
    try {
      const r = await axios.post(`${API}/api/admin/di-po/build`, basePayload(), { headers: authHeader });
      setPreviews(r.data.drafts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  const sendDrafts = async () => {
    if (!chosen.length) return toast.error("Select at least one vendor");
    const missing = chosen.filter((c) => !(c.po_number || "").trim());
    if (missing.length) return toast.error("Enter a Sterile Mate PO # for each selected vendor");
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/admin/di-po/outlook-draft`, basePayload(), { headers: authHeader });
      const { drafted, total, results } = r.data;
      if (drafted) {
        toast.success(`${drafted} of ${total} PO draft${total === 1 ? "" : "s"} created in Outlook — attach the PO PDF and send`);
        (results || []).filter((x) => x.ok && x.web_link).forEach((x) => window.open(x.web_link, "_blank"));
        onSent?.();
        onClose?.();
      } else {
        const why = results?.[0]?.detail || "Outlook drafting unavailable";
        toast.error(`${why} — opening your mail app instead`);
        (results || []).forEach((x) => x.mailto_url && window.open(x.mailto_url));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally {
      setSending(false);
    }
  };

  const field = (label, key, placeholder, testid) => (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">{label}</label>
      <Input
        value={form[key] ?? ""}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className="h-9 text-sm"
        data-testid={testid}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-6xl my-6 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="di-po-dialog">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-cyan-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-100 text-cyan-700 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-slate-900">Send DI Exchange PO</h3>
              <p className="text-xs text-slate-500">
                One PO per vendor · drafted in Outlook so you attach the PO PDF
                {ctx?.source_label ? ` · ${ctx.source_label}` : ""}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="di-po-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !form ? (
          <div className="px-6 py-16 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading vendors and site details…
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-0">
            {/* LEFT — vendor picker + per-vendor PO numbers */}
            <div className="border-r border-slate-200 max-h-[72vh] overflow-y-auto">
              <div className="p-4 sticky top-0 bg-white border-b border-slate-200 z-10 space-y-2">
                <div className="flex gap-1.5" data-testid="di-po-vendor-tabs">
                  <button
                    type="button"
                    onClick={() => setVendorTab("supplier")}
                    className={`flex-1 px-2 py-1.5 rounded text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-colors ${vendorTab === "supplier" ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-testid="di-po-tab-supplier"
                  >
                    <Building2 className="w-3.5 h-3.5" /> Suppliers ({ctx?.suppliers?.length || 0})
                  </button>
                  <button
                    type="button"
                    onClick={() => setVendorTab("contractor")}
                    className={`flex-1 px-2 py-1.5 rounded text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-colors ${vendorTab === "contractor" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-testid="di-po-tab-contractor"
                  >
                    <Wrench className="w-3.5 h-3.5" /> Contractors ({ctx?.contractors?.length || 0})
                  </button>
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, category…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9 text-sm"
                    data-testid="di-po-vendor-search"
                  />
                </div>
                <p className="text-[11px] text-slate-500">
                  {chosen.length} selected · each needs its own Sterile Mate PO #
                </p>
              </div>

              <div className="divide-y divide-slate-100">
                {visible.map((v) => {
                  const sel = !!picked[v.id];
                  return (
                    <div key={v.id} className={`px-4 py-2.5 ${sel ? "bg-cyan-50/60" : "hover:bg-slate-50"}`}>
                      <div className="flex items-start gap-2.5 cursor-pointer" onClick={() => toggle(v)}>
                        <input
                          type="checkbox"
                          checked={sel}
                          readOnly
                          className="mt-1 pointer-events-none"
                          data-testid={`di-po-vendor-${v.id}`}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block font-semibold text-sm text-slate-900 truncate">{v.name}</span>
                          <span className="block text-[11px] text-slate-500 truncate">{v.email}</span>
                        </span>
                      </div>
                      {sel && (
                        <div className="mt-2 ml-6">
                          <Input
                            value={picked[v.id].po_number}
                            onChange={(e) => setPo(v.id, e.target.value)}
                            placeholder="Sterile Mate PO # for this vendor (e.g. 1567)"
                            className="h-8 text-sm font-mono"
                            data-testid={`di-po-number-${v.id}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {!visible.length && (
                  <p className="px-4 py-10 text-center text-sm text-slate-400">No vendors match that search.</p>
                )}
              </div>
            </div>

            {/* RIGHT — order + site details */}
            <div className="max-h-[72vh] overflow-y-auto p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {field("Hospital Reference PO #", "hospital_po_number", "10000-002595062", "di-po-hospital-po")}
                {field("SKU / Service Code", "service_code", "REPAIR", "di-po-service-code")}
              </div>
              {field("Service Requested", "service_requested", "Exchange of Primary & Secondary DI Tanks", "di-po-service")}
              <div className="grid grid-cols-3 gap-3">
                {field("Quantity", "quantity", "2", "di-po-qty")}
                {field("Unit", "quantity_label", "Tanks", "di-po-qty-label")}
                {field("Replenishment Type", "replenishment_type", "Standard Exchange", "di-po-replenishment")}
              </div>

              <div className="pt-1 border-t border-slate-200">
                <p className="text-[11px] font-bold uppercase tracking-wide text-cyan-700 mt-3 mb-2">
                  Delivery &amp; check-in — saved for {ctx?.hospital_name || "this hospital"}
                </p>
                <div className="space-y-3">
                  {field("Location", "location", "Lyndon B. Johnson Hospital (Main Campus) — 5656 Kelley St, Houston, TX 77026", "di-po-location")}
                  {field("Destination", "destination", "4th Floor Sterile Processing Department (SPD) — Water Treatment Room", "di-po-destination")}
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Check-In Protocol</label>
                    <Textarea
                      value={form.checkin_protocol}
                      onChange={(e) => setForm((f) => ({ ...f, checkin_protocol: e.target.value }))}
                      rows={3}
                      placeholder="Upon arrival, the service technician must stop by the Biomed Office on the 1st Floor, sign in, and reference the Hospital PO before proceeding."
                      className="text-sm"
                      data-testid="di-po-checkin"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {field("On-Site Contact", "poc_name", "George Broughton III", "di-po-poc-name")}
                    {field("Direct / Mobile", "poc_phone", "(323) 243-9139", "di-po-poc-phone")}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-1">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.urgent}
                    onChange={(e) => setForm((f) => ({ ...f, urgent: e.target.checked }))}
                    data-testid="di-po-urgent"
                  />
                  Mark as urgent
                </label>
                <div className="flex-1">{field("Signed by", "sign_off_name", "George Broughton III", "di-po-signoff")}</div>
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Extra note (optional)</label>
                <Textarea
                  value={form.extra_note}
                  onChange={(e) => setForm((f) => ({ ...f, extra_note: e.target.value }))}
                  rows={2}
                  className="text-sm"
                  data-testid="di-po-extra-note"
                />
              </div>

              <div className="rounded-md bg-cyan-50 border border-cyan-200 px-3 py-2.5 text-xs text-cyan-900 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Each vendor gets a <strong>separate</strong> draft addressed only to them, referencing their own
                  Sterile Mate PO #. Attach the PO PDF in Outlook before sending. Site details above are saved for
                  this hospital and auto-fill next time.
                </span>
              </div>

              {previews.length > 0 && (
                <div className="space-y-2" data-testid="di-po-previews">
                  {previews.map((d) => (
                    <div key={`${d.vendor_id}-${d.po_number}`} className="border border-slate-200 rounded-md overflow-hidden">
                      <div className="bg-slate-50 px-3 py-1.5 text-[11px] text-slate-600 font-semibold">
                        {d.vendor_name} · {d.to?.[0]} · {d.subject}
                      </div>
                      <pre className="px-3 py-2 text-[11px] text-slate-700 whitespace-pre-wrap font-sans max-h-48 overflow-y-auto">{d.body_text}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-end gap-2 bg-slate-50">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="di-po-cancel">Cancel</Button>
          <Button variant="outline" size="sm" onClick={preview} disabled={!chosen.length} className="gap-1.5" data-testid="di-po-preview">
            <FileText className="w-3.5 h-3.5" /> Preview
          </Button>
          <Button
            size="sm"
            onClick={sendDrafts}
            disabled={sending || !chosen.length}
            className="bg-cyan-600 hover:bg-cyan-700 gap-1.5"
            data-testid="di-po-send"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Create {chosen.length || ""} Outlook Draft{chosen.length === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
    </div>
  );
}
